import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../api';
import { PreviewResult, Vacation360, VacationRequestItem } from '../api/ferias';
import { usePreverFerias, useReenviarFerias, useSolicitarFerias } from '../hooks/ferias';
import { formatDate, vacationEnd, vacationFormErrors } from './format';
import { PessoasModal } from './PessoasModal';

type Parcela = { startDate: string; days: number };

export type SolicitarModo =
  | { kind: 'nova' }
  | { kind: 'reenviar'; request: VacationRequestItem }
  | { kind: 'reprogramar'; request: VacationRequestItem };

interface Props {
  open: boolean;
  onClose: () => void;
  visao: Vacation360;
  /** Nulo no autosserviço; o vínculo quando o RH solicita. */
  bondId: string | null;
  asHr: boolean;
  modo: SolicitarModo;
}

/** Solicitação de férias (US-GP-012): parcelas, venda e 13º, com pré-visualização de erros e conflitos. */
export function SolicitarFeriasModal({ open, onClose, visao, bondId, asHr, modo }: Props) {
  const toast = useToast();
  const prever = usePreverFerias(bondId);
  const solicitar = useSolicitarFerias(bondId);
  const reenviar = useReenviarFerias(asHr);
  const policy = visao.policy ?? { maxInstallments: 3, allowSell: false, maxSellDays: 0, allow13thAdvance: false, minNoticeDays: 30 };
  const abertos = useMemo(
    () => visao.periods.filter((p) => !['em_aquisicao', 'perdido', 'encerrado', 'concluido'].includes(p.status)),
    [visao.periods],
  );

  const [periodId, setPeriodId] = useState('');
  const [parcelas, setParcelas] = useState<Parcela[]>([{ startDate: '', days: 30 }]);
  const [sellDays, setSellDays] = useState(0);
  const [advance13th, setAdvance13th] = useState(false);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = modo.kind === 'nova' ? null : modo.request;
    setPeriodId(base?.periodId ?? abertos[0]?.id ?? '');
    setParcelas(base
      ? base.installments.filter((i) => i.status !== 'cancelada').map((i) => ({ startDate: i.startDate, days: i.days }))
      : [{ startDate: '', days: 30 }]);
    setSellDays(base?.sellDays ?? 0);
    setAdvance13th(base?.advance13th ?? false);
    setNotes('');
    setPreview(null);
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodo = visao.periods.find((p) => p.id === periodId);
  // No reenvio e na reprogramação, o saldo da solicitação original volta para o cálculo.
  const devolvido = modo.kind === 'nova' ? 0
    : modo.request.installments.filter((i) => i.status !== 'cancelada').reduce((s, i) => s + i.days, 0) + modo.request.sellDays;
  const disponivel = (periodo?.available ?? 0) + devolvido;
  const maxVenda = periodo?.maxSellDays ?? 0;

  const payload = () => ({
    periodId,
    installments: parcelas.filter((p) => p.startDate && p.days > 0).map((p) => ({ startDate: p.startDate, endDate: vacationEnd(p.startDate, p.days) })),
    sellDays: sellDays || undefined,
    advance13th: advance13th || undefined,
    notes: notes.trim() || undefined,
    ...(modo.kind === 'reprogramar' ? { replacesRequestId: modo.request.id } : {}),
  });

  const setParcela = (i: number, patch: Partial<Parcela>) => {
    setPreview(null);
    setParcelas((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  };

  const errosLocais = () => {
    if (!periodId) return ['Escolha o período aquisitivo.'];
    return vacationFormErrors(parcelas, sellDays, disponivel, policy.maxInstallments);
  };

  async function verificar() {
    setError(null);
    const local = errosLocais();
    if (local.length) return setError(local.join(' '));
    try {
      setPreview(await prever.mutateAsync(payload()));
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível verificar a solicitação.'));
    }
  }

  async function enviar() {
    setError(null);
    const local = errosLocais();
    if (local.length) return setError(local.join(' '));
    try {
      const p = payload();
      const r = modo.kind === 'reenviar'
        ? await reenviar.mutateAsync({
          id: modo.request.id,
          payload: { installments: p.installments, sellDays: p.sellDays, advance13th: p.advance13th, notes: p.notes },
        })
        : await solicitar.mutateAsync(p);
      toast.success(r.status === 'aprovada' ? 'Férias aprovadas.' : 'Solicitação enviada para aprovação.');
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível enviar a solicitação.'));
    }
  }

  const titulo = modo.kind === 'nova' ? 'Solicitar férias' : modo.kind === 'reenviar' ? 'Ajustar e reenviar' : 'Reprogramar férias';
  const ultimoComentario = modo.kind === 'reenviar' ? modo.request.approvals[modo.request.approvals.length - 1]?.comment : null;
  const totalGozo = parcelas.reduce((s, p) => s + (p.startDate ? p.days : 0), 0);

  return (
    <PessoasModal
      open={open} size="lg" title={titulo}
      description={`Até ${policy.maxInstallments} parcela(s): uma com 14 dias ou mais e as demais com 5 ou mais. Início com ${policy.minNoticeDays} dias de antecedência e fora dos 2 dias antes de feriado ou domingo.`}
      submitLabel="Enviar" isSubmitting={solicitar.isPending || reenviar.isPending} submitError={error} onClose={onClose} onSubmit={enviar}
    >
      {ultimoComentario && <p className="rounded bg-amber-50 p-2 text-sm text-amber-900">Ajuste pedido: {ultimoComentario}</p>}
      <AutocompleteSelect
        label="Período aquisitivo"
        options={abertos.map((p) => ({
          value: p.id, label: `${formatDate(p.acquisitionStart)} a ${formatDate(p.acquisitionEnd)} · ${p.available} dias disponíveis`,
        }))}
        value={periodId}
        onChange={(v) => { setPeriodId(v); setPreview(null); }}
        disabled={modo.kind !== 'nova'}
      />
      <p className="text-xs text-gray-600">
        Saldo para esta solicitação: <strong>{disponivel} dias</strong>
        {periodo && ` · concessivo até ${formatDate(periodo.concessionEnd)}`}
      </p>

      <div className="space-y-2">
        {parcelas.map((p, i) => (
          <div key={i} className="grid grid-cols-1 items-end gap-2 rounded border border-gray-200 p-2 sm:grid-cols-[1fr_6rem_1fr_auto]">
            <TextInput label={`Início da parcela ${i + 1}`} type="date" value={p.startDate} onChange={(e) => setParcela(i, { startDate: e.target.value })} />
            <TextInput label="Dias" type="number" min={5} max={30} value={String(p.days)}
              onChange={(e) => setParcela(i, { days: Number(e.target.value) || 0 })} />
            <p className="pb-2 text-sm text-gray-600">{p.startDate && p.days > 0 ? `até ${formatDate(vacationEnd(p.startDate, p.days))}` : ''}</p>
            {parcelas.length > 1 ? (
              <button type="button" className="pb-2 text-gray-500" aria-label={`Remover parcela ${i + 1}`}
                onClick={() => { setPreview(null); setParcelas((ps) => ps.filter((_, j) => j !== i)); }}>
                <Trash2 className="h-4 w-4" />
              </button>
            ) : <span />}
          </div>
        ))}
        {parcelas.length < policy.maxInstallments && (
          <Button size="sm" variant="secondary" onClick={() => setParcelas((ps) => [...ps, { startDate: '', days: 5 }])}>
            <Plus className="mr-1 h-4 w-4" /> Parcela
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {maxVenda > 0 && (
          <TextInput label={`Dias a vender (até ${maxVenda})`} type="number" min={0} max={maxVenda} value={String(sellDays)}
            onChange={(e) => { setPreview(null); setSellDays(Math.max(0, Number(e.target.value) || 0)); }} />
        )}
        {policy.allow13thAdvance && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" checked={advance13th} onChange={(e) => setAdvance13th(e.target.checked)} /> Adiantar a 1ª parcela do 13º
          </label>
        )}
      </div>
      <Textarea label="Observação (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

      <div className="space-y-2">
        <Button size="sm" variant="secondary" onClick={verificar} disabled={prever.isPending}>Verificar regras e conflitos</Button>
        {preview && (
          <div className="space-y-1 text-sm">
            {preview.errors.length === 0
              ? <p className="text-green-700">Nenhum impedimento encontrado.</p>
              : <ul className="list-disc pl-5 text-red-700">{preview.errors.map((e) => <li key={e}>{e}</li>)}</ul>}
            {preview.conflicts.length > 0 && (
              <ul className="list-disc pl-5 text-amber-800">{preview.conflicts.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">Total: {totalGozo} dias de gozo{sellDays ? ` + ${sellDays} vendidos` : ''}</p>
    </PessoasModal>
  );
}
